import { Box, CircularProgress, LinearProgress, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshIcon from "../../../../../../assets/Fibo/icons/RefreshIcon";
import ClockIcon from "../../../../../../assets/TailoredGeneration/icons/tg-clock.svg";
import DeleteIcon from "../../../../../../assets/images/icons/DeleteIcon";
import ErrorCircle from "../../../../../../assets/images/icons/tg-error-circle.svg";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton";
import BriaIconButton from "../../../../../../components/common/BriaIconButton/BriaIconButton";
import BriaTooltip from "../../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import FileUploader from "../../../../../../components/common/FileUploader/FileUploader";
import { useAppStore } from "../../../../../../hooks/useStores";
import styles from "./NewTgDatasetEditor.module.scss";

type Props = {
	onContinue: () => void;
};

const NewTgDatasetEditor = ({ onContinue }: Props) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.datasetPanel" });
	const { tgStore } = useAppStore();
	const { tgDatasetStore } = tgStore;
	const [loadingUploadButton, setLoadingUploadButton] = useState(false);

	const getReadableError = (err?: any): string => {
		if (!err) return "";
		const raw = typeof err.message === "string" ? err.message : err?.toString?.() || "";
		try {
			const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
			if (parsed?.errors && Array.isArray(parsed.errors)) {
				const msgs = parsed.errors
					.map((e: any) => e?.msg || e?.message)
					.filter(Boolean)
					.join(" • ");
				return msgs || raw;
			}
			if (parsed?.message && typeof parsed.message === "string") {
				return parsed.message;
			}
			return raw;
		} catch {
			const data = (err as any)?.response?.data;
			if (data?.errors && Array.isArray(data.errors)) {
				const msgs = data.errors
					.map((e: any) => e?.msg || e?.message)
					.filter(Boolean)
					.join(" • ");
				return msgs || raw;
			}
			if (typeof data?.message === "string") return data.message;
			return raw;
		}
	};

	const uploaded = tgDatasetStore.datasetForm.images.filter((img) => !img.uploading && !img.error).length;
	const total = tgDatasetStore.datasetForm.images.length;
	const uploading = tgDatasetStore.datasetForm.images.filter((img) => img.uploading).length;
	const failed = tgDatasetStore.datasetForm.images.filter((img) => !!img.error && !img.uploading).length;

	const [batchTotal, setBatchTotal] = useState(0);
	const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set());

	useEffect(() => {
		setBatchTotal((prev) => {
			if (uploading === 0) return 0;
			return uploading > prev ? uploading : prev;
		});
	}, [uploading]);

	const batchProcessed = batchTotal > 0 ? batchTotal - uploading : 0;
	const progressPct =
		batchTotal > 0 ? Math.round((batchProcessed / batchTotal) * 100) : uploading === 0 && total > 0 ? 100 : 0;
	const etaSeconds = uploading * 2;

	const formatEta = (totalSeconds: number): string => {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = Math.round(totalSeconds % 60);
		if (minutes === 0) return t("etaSeconds", { seconds });
		if (seconds === 0) return t("etaMinutes", { minutes });
		return t("etaMinutesSeconds", { minutes, seconds });
	};

	const handleUploadMore = async (e: ChangeEvent<HTMLInputElement>) => {
		const files: File[] = Array.from(e.target.files || []);
		e.target.value = "";
		if (!files.length) return;
		setLoadingUploadButton(true);
		await tgDatasetStore.uploadImages(tgDatasetStore.datasetForm.id, files);
		setLoadingUploadButton(false);
	};

	const canContinue = useMemo(() => {
		return uploaded >= 5 && uploading === 0;
	}, [uploaded, uploading]);

	const handleContinue = () => {
		const remaining = tgDatasetStore.datasetForm.images.filter((img) => !img.error);
		tgDatasetStore.handleFormChange("images", remaining as any);
		tgDatasetStore.calculateAndSetBasicSampleImages(10);
		onContinue();
	};

	return (
		<Box className={styles.container}>
			<Box className={styles.header}>
				<Typography className={styles.title}>{t("uploadedTitle")}</Typography>
				<Box className={styles.progressWrapper}>
					<LinearProgress
						color="primary"
						variant="determinate"
						value={progressPct}
						className={styles.progressBar}
					/>
					<Box className={styles.etaWrap}>
						<img src={ClockIcon} width={16} height={16} />
						<Typography className={styles.eta}>{formatEta(etaSeconds)}</Typography>
					</Box>
				</Box>
			</Box>

			<Box className={styles.grid}>
				{tgDatasetStore.datasetForm.images.map((image) => (
					<Box
						key={image.id}
						className={`${styles.tile} ${!image.uploading && image.error ? styles.hasError : ""}`}
					>
						<Box
							key={image.id}
							className={`${styles.gridItem} ${!image.uploading && image.error ? styles.error : ""}`}
						>
							<img className={styles.thumb} src={image.thumbnail_url || image.image_url} />
							{image.uploading && (
								<Box className={styles.uploadingOverlay}>
									<CircularProgress size={28} thickness={5} />
								</Box>
							)}
							{!image.uploading && !!image.error && (
								<Box className={styles.overlay}>
									<Box className={styles.overlayActions}>
										<BriaIconButton
											title={t("retry")}
											onClick={async () => {
												if (image.file) {
													await tgDatasetStore.uploadImage(
														tgDatasetStore.datasetForm.id,
														image.file,
														image.id,
													);
												}
											}}
										>
											<RefreshIcon />
										</BriaIconButton>
										<BriaIconButton
											title={t("delete")}
											disabled={deletingIds.has(image.id)}
											onClick={async () => {
												setDeletingIds((prev) => new Set(prev).add(image.id));
												try {
													const remaining = tgDatasetStore.datasetForm.images.filter(
														(img) => img.id !== image.id,
													);
													tgDatasetStore.handleFormChange("images", remaining as any);
												} finally {
													setDeletingIds((prev) => {
														const next = new Set(prev);
														next.delete(image.id);
														return next;
													});
												}
											}}
										>
											{deletingIds.has(image.id) ? (
												<CircularProgress size={18} thickness={5} />
											) : (
												<DeleteIcon />
											)}
										</BriaIconButton>
									</Box>
								</Box>
							)}
							{!image.uploading && !image.error && (
								<Box className={styles.hoverDelete}>
									<BriaIconButton
										className={styles.deleteBtn}
										title={t("delete")}
										disabled={deletingIds.has(image.id)}
										onClick={async () => {
											setDeletingIds((prev) => new Set(prev).add(image.id));
											try {
												await tgDatasetStore.deleteImage(
													tgDatasetStore.datasetForm.id,
													image.id,
												);
											} finally {
												setDeletingIds((prev) => {
													const next = new Set(prev);
													next.delete(image.id);
													return next;
												});
											}
										}}
									>
										{deletingIds.has(image.id) ? (
											<CircularProgress size={18} thickness={5} />
										) : (
											<DeleteIcon />
										)}
									</BriaIconButton>
								</Box>
							)}
						</Box>
						{!image.uploading && !!image.error && (
							<Box className={styles.errorLine}>
								<span className={styles.statusText}>{t("failedSeeError")}</span>
								<BriaTooltip title={getReadableError(image.error)}>
									<Box className={styles.imgBox}>
										<img src={ErrorCircle} width={17} height={17} />
									</Box>
								</BriaTooltip>
							</Box>
						)}
						{(!image.error || image.uploading) && <Box className={styles.errorLine} />}
					</Box>
				))}
			</Box>

			<Box className={styles.footer}>
				<Box className={styles.counts}>
					<Typography className={styles.countText}>
						{t("uploadedCount")}{" "}
						<Typography component="span" className={styles.countValue}>
							{uploaded}
						</Typography>
					</Typography>
					<Typography className={styles.countText}>
						{t("failedCount")}{" "}
						<Typography component="span" className={styles.countValue}>
							{failed}
						</Typography>
					</Typography>
				</Box>
				<Box className={styles.actions}>
					<FileUploader
						buttonType="secondarySmall"
						buttonText={t("uploadMore")}
						onUpload={handleUploadMore}
						maxFilesLimit={200 - total}
						inputProps={{ accept: "image/png, image/jpg, image/jpeg, image/webp", multiple: true }}
						loading={loadingUploadButton || uploading > 0}
						disabled={uploading > 0 || loadingUploadButton}
					/>
					<BriaTooltip title={t("continueTooltip")} disable={canContinue || uploaded >= 5}>
						<Box>
							<BriaButton buttonType="primaryMedium" onClick={handleContinue} disabled={!canContinue}>
								{t("continue")}
							</BriaButton>
						</Box>
					</BriaTooltip>
				</Box>
			</Box>
		</Box>
	);
};

export default observer(NewTgDatasetEditor);
